import React, { useState, useEffect } from 'react';
import Navigation from './Navigation';
import SearchBar from './SearchBar';
import CreatorVodSection from './CreatorClipSection';
import { serverURL } from "../constants";

function DigestHome({ setAuthenticated }) {
  const [creators, setCreators] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCountdown, setRetryCountdown] = useState(0);

  const fetchCreators = async () => {
    try {
      setIsLoading(true);
      setError(null);
      setRetryCountdown(0);
      
      const response = await fetch(serverURL + "/clips", {
        credentials: "include"
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Handle rate limiting specifically
        if (response.status === 429) {
          const retryAfter = errorData.retryAfter || 60;
          const message = errorData.rateLimitInfo?.message || 'Rate limit exceeded. Please try again later.';
          
          setError({
            type: 'rate_limit',
            message: message,
            retryAfter: retryAfter
          });
          
          // Start countdown for retry
          setRetryCountdown(retryAfter);
          const countdownInterval = setInterval(() => {
            setRetryCountdown(prev => {
              if (prev <= 1) {
                clearInterval(countdownInterval);
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
          
          return;
        }
        
        // Handle other errors
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Received data from server:', data);
      console.log('Data type:', typeof data);
      console.log('Is array:', Array.isArray(data));
      console.log('Data length:', data ? data.length : 'undefined');
      
      // Ensure data is an array and handle edge cases
      let creatorsData = [];
      if (Array.isArray(data)) {
        creatorsData = data;
      } else if (data && typeof data === 'object') {
        // If data is an object, try to extract creators from it
        creatorsData = data.creators || data.data || [];
      } else {
        console.warn('Unexpected data format:', data);
        creatorsData = [];
      }
      
      console.log('Processed creators data:', creatorsData);
      
      setCreators(creatorsData);
    } catch (error) {
      console.error('Error fetching VODs:', error);
      setError({
        type: 'general',
        message: error.message || 'An error occurred while fetching data. Please try again later.'
      });
      setCreators([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = () => {
    if (retryCountdown === 0) {
      fetchCreators();
    }
  };

  const handleSearch = searchQuery => {
    if (!searchQuery || searchQuery.trim() === '') {
      console.warn('Empty search query provided');
      return;
    }

    const usernames = searchQuery.split(',').map(name => name.trim()).filter(name => name.length > 0);
    
    if (usernames.length === 0) {
      console.warn('No valid usernames found in search query');
      return;
    }

    console.log('Searching for usernames:', usernames);

    fetch(serverURL + '/clips/usernames', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ usernames }),
      credentials: 'include',
    }).then(() => {
      fetchCreators();
    }).catch((error) => {
      console.error('Error during search:', error);
      setError({
        type: 'search',
        message: 'Failed to update search. Please try again.'
      });
    });
  };

  useEffect(() => {
    fetchCreators();
  }, []);

  // Render error state
  if (error) {
    return (
      <div className="app-container">
        <Navigation setAuthenticated={setAuthenticated} />
        <SearchBar onSearch={handleSearch}/>
        <section className="vods-section">
          <div className="vods-container">
            <div className="error-state">
              <div className="error-icon">⚠️</div>
              <h2 className="error-title">
                {error.type === 'rate_limit' ? 'Rate Limit Exceeded' : 'Error'}
              </h2>
              <p className="error-description">{error.message}</p>
              
              {error.type === 'rate_limit' && retryCountdown > 0 ? (
                <div className="retry-countdown">
                  <p>You can try again in <span className="countdown-timer">{retryCountdown}</span> seconds</p>
                  <div className="countdown-progress">
                    <div 
                      className="countdown-bar" 
                      style={{ 
                        width: `${((error.retryAfter - retryCountdown) / error.retryAfter) * 100}%` 
                      }}
                    ></div>
                  </div>
                </div>
              ) : (
                <button 
                  className="retry-button"
                  onClick={handleRetry}
                  disabled={retryCountdown > 0}
                >
                  {retryCountdown > 0 ? `Wait ${retryCountdown}s` : 'Try Again'}
                </button>
              )}
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Navigation setAuthenticated={setAuthenticated} />
      <SearchBar onSearch={handleSearch}/>
      {isLoading ? (
        <section className="vods-section">
          <div className="vods-container">
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <h2 className="loading-title">Loading VODs...</h2>
              <p className="loading-description">Please wait while we fetch your data.</p>
            </div>
          </div>
        </section>
      ) : (
        <CreatorVodSection creators={creators || []}/>
      )}
    </div>
  );
}

export default DigestHome;

